import { createColumnHelper, useReactTable, getCoreRowModel } from '@tanstack/react-table';
import { EntityTable } from '@/components/table/entity-table.js';
import { InfoCard } from '@/components/info-card.js';
import { Badge } from '@/components/ui/badge.js';
import {
  adjustMinimalClassTimeAcrossTZ,
  CoachViewableStudentGroupWithScheduleAndLevel,
  VideoConferenceTypeLabels,
} from '@airreading/common/data';
import { formatPlainDate, FrequentTimeZones } from '@airreading/common';
import { formatScheduleBreaksForDisplay } from '@/lib/meeting-block-utils.js';
import { formatStudentGrades } from '@/lib/student-group-utils.js';
import { formatStudentClassTimeAbbr } from '../../scheduling/schedules/-components/formatting.js';
import { Check } from 'lucide-react';

function coachDisplayName(name: string | null | undefined): string {
  return name?.trim() || '-';
}

export function ClassesCard({ classes }: { classes: CoachViewableStudentGroupWithScheduleAndLevel[] }) {
  const columnHelper = createColumnHelper<CoachViewableStudentGroupWithScheduleAndLevel>();

  const columns = [
    columnHelper.accessor((row) => row.classTime, {
      id: 'classTimes',
      header: 'Class times (CT)',
      enableSorting: false,
      cell: ({ getValue }) => {
        const classTime = getValue();
        return (
          <Badge variant="outline" className="bg-[#F1F1F1] font-normal">
            {formatStudentClassTimeAbbr(
              adjustMinimalClassTimeAcrossTZ(
                classTime,
                classTime.schedule.school.org.timeZone,
                FrequentTimeZones.UsCentral,
              ),
            )}
          </Badge>
        );
      },
    }),
    columnHelper.accessor(
      (row) => {
        return row.overrideStartPlainDate || row.classTime?.schedule?.startPlainDate;
      },
      {
        id: 'startDate',
        header: 'Start date',
        enableSorting: false,
        cell: ({ getValue }) => {
          const value = getValue();
          return value ? formatPlainDate(value) : '';
        },
      },
    ),
    columnHelper.accessor(
      (row) => {
        // A removed teacher stops before the schedule ends, so their last class is the end date.
        return row.teacherPortalStatus === 'removed'
          ? row.teacherPortalLastClassPlainDate
          : row.overrideEndPlainDate || row.classTime?.schedule?.endPlainDate;
      },
      {
        id: 'endDate',
        header: 'End date',
        enableSorting: false,
        cell: ({ getValue }) => {
          const value = getValue();
          return value ? formatPlainDate(value) : '';
        },
      },
    ),
    columnHelper.accessor((row) => row.futureSessions ?? 0, {
      id: 'sessionsLeft',
      header: 'Sessions left',
      enableSorting: false,
      cell: ({ getValue }) => getValue(),
    }),
    columnHelper.accessor((row) => row.classTime.schedule.school.org.videoConferenceType, {
      id: 'meetingType',
      header: 'Meeting type',
      enableSorting: false,
      cell: ({ getValue }) => VideoConferenceTypeLabels[getValue()],
    }),
    columnHelper.accessor((row) => row.students ?? [], {
      id: 'students',
      header: 'Students',
      enableSorting: false,
      cell: ({ getValue }) => {
        return (
          <div className="flex flex-col gap-1">
            {getValue().map((student) => (
              <Badge key={student.id} variant="outline" className="text-left rounded-sm px-1 w-fit">
                {coachDisplayName(student.coachFacingDisplayName)}
              </Badge>
            ))}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => row.grades ?? [], {
      id: 'grades',
      header: 'Grades',
      enableSorting: false,
      cell: ({ getValue }) => formatStudentGrades(getValue()),
    }),
    columnHelper.accessor((row) => row.classTime?.schedule?.school?.name ?? '', {
      id: 'school',
      header: 'School',
      enableSorting: false,
      cell: ({ getValue }) => getValue(),
    }),
    columnHelper.accessor((row) => row.classTime?.schedule?.school?.org?.name ?? '', {
      id: 'district',
      header: 'District',
      enableSorting: false,
      cell: ({ getValue }) => getValue(),
    }),

    columnHelper.accessor((row) => formatScheduleBreaksForDisplay(row.classTime.schedule.scheduleBreaks), {
      id: 'breaks',
      header: 'Break(s)',
      enableSorting: false,
      cell: ({ getValue }) => getValue(),
    }),
    columnHelper.accessor('droppedByTeacher', {
      id: 'droppedByTeacher',
      header: 'Dropped by teacher',
      enableSorting: false,
      cell: ({ getValue }) => (getValue() ? <Check /> : null),
    }),
  ];
  const table = useReactTable({
    data: classes,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const droppedByTeacherCount = classes.filter((c) => c.droppedByTeacher).length;

  return (
    <InfoCard
      headerText={
        <>
          Current groups{' '}
          <span className="text-sm font-normal text-gray-500 ml-2">(Dropped by teacher: {droppedByTeacherCount})</span>
        </>
      }
    >
      <EntityTable table={table} emptyStateClassName="translate-y-0" />
    </InfoCard>
  );
}
